import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import ChevronBackBtn from '../../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { AuthContext } from '../../../shared/context/auth-context';
import { GreenCheckOverlay } from '../../../shared/modal/greenCheckOverlay/GreenCheckOverlay';
import WhitePopUpNoTitle from '../../../shared/modal/whitePopUp/WhitePopUpNoTitle';
import ProfileDepartmentFields from './ProfileDepartmentFields';
import ProfileEmailField from './ProfileEmailField';
import ProfileNameField from './ProfileNameField';
import ProfileRegionFields from './ProfileRegionFields';
import { useAccountDetailsState } from './useAccountDetailsState';

type AccountDetailsRightMenuProps = {
  hide: boolean;
  profile: any;
  changeMenu: (_menuIndex: number, _action: string) => void;
  saveChanges: (_profileData: any) => void;
  activeSubMenuId?: number;
};

export function AccountDetailsRightMenu({
  hide,
  profile: profileProp,
  changeMenu,
  saveChanges,
  activeSubMenuId = 51,
}: AccountDetailsRightMenuProps) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const {
    modalIsOpen,
    modalTextContent,
    profile,
    activeDept,
    activeSubDept,
    subDeptsArr,
    activeRegion,
    regionArr,
    regionGroupArr,
    regionGroup,
    regionsOptions,
    showGreenCheck,
    modalClickHandler,
    handleEmailChange,
    handleNameChange,
    saveProfile,
    handleSelectRegionGroup,
    handleSelectRegion,
    handleSelectSubDept,
    handleSelectDept,
    // Error handling
    errorSummary,
    getFieldError,
    hasFieldError,
    isAfterSubmit,
  } = useAccountDetailsState({
    profile: profileProp,
    saveChanges,
  });

  // Field mapping for error summary
  const fieldMapping = {
    name: t('R027'), // Name
    email: t('R023'), // Email
    region: t('S031'), // Region
    department: t('S030'), // Department
    subDepartment: t('S029'), // Sub Department
  };

  return (
    <>
      {modalIsOpen && (
        <WhitePopUpNoTitle
          returnFocus
          popupContents={modalTextContent}
          ctaBtn={t('T015')}
          handleSubmit={modalClickHandler}
        />
      )}
      {showGreenCheck && <GreenCheckOverlay text={t('saved_changes_successfully')} />}
      {profileProp && (
        <fieldset
          role="tabpanel"
          id="panel-account-details"
          aria-labelledby="tab-account-details-title"
          tabIndex={activeSubMenuId === 51 ? 0 : -1}
          hidden={activeSubMenuId !== 51}
          className="edit-field fs-exclude account_details_container"
        >
          {hide && (
            <div className="btn-cont ">
              <ChevronBackBtn onClick={() => changeMenu(0, 'back')} />
            </div>
          )}
          <h2 className="edit-title" id="tab-account-details-title">
            {t('S025')}
          </h2>

          {errorSummary.length > 0 && isAfterSubmit && (
            <Alert
              id="error_summary_account_details"
              isAssessive
              isVisible
              message={
                <ol>
                  {errorSummary
                    .sort((a, b) => {
                      const order = ['name', 'email', 'region', 'department', 'subDepartment'];
                      return order.indexOf(a) - order.indexOf(b);
                    })
                    .map((error) => (
                      <li key={error}>
                        <a href={`#${error}`}>
                          {fieldMapping[error as keyof typeof fieldMapping] || t('error')}
                        </a>
                      </li>
                    ))}
                </ol>
              }
              title={{
                text:
                  errorSummary.length > 1
                    ? t('error.summary.title.fields.many', { errorCount: errorSummary.length })
                    : t('error.summary.title.fields.one'),
                showTitle: true,
              }}
              type="error"
            />
          )}
          <form onSubmit={saveProfile}>
            <ProfileNameField
              label={t('R027')}
              name={profile.name}
              handleNameChange={handleNameChange}
              errorMessage={t('S054')}
              hasError={hasFieldError('nameRequired')}
              errorText={getFieldError('nameRequired')}
              isAfterSubmit={isAfterSubmit}
            />
            <ProfileEmailField
              label={t('R023')}
              email={profile.email}
              handleEmailChange={handleEmailChange}
              errorMessage={t('S055')}
              auth={auth}
              hasError={hasFieldError('emailRequired') || hasFieldError('emailInvalid')}
              errorText={getFieldError('emailRequired') || getFieldError('emailInvalid')}
              isAfterSubmit={isAfterSubmit}
            />
            <ProfileRegionFields
              label={{ region: t('S031'), regionGroup: t('S032') }}
              defaultValue={{ region: t('S123'), regionGroup: t('S122') }}
              options={{
                region: regionGroupArr?.length ? regionsOptions : regionArr,
                regionGroup: regionGroupArr,
              }}
              value={{ region: activeRegion, regionGroup }}
              onChange={{
                region: handleSelectRegion,
                regionGroup: handleSelectRegionGroup,
              }}
              hasRegionError={hasFieldError('regionRequired')}
              hasRegionGroupError={false} // Region group is not required
              regionErrorText={getFieldError('regionRequired')}
              regionGroupErrorText=""
              isAfterSubmit={isAfterSubmit}
            />
            <ProfileDepartmentFields
              profile={profile}
              active={{ department: activeDept, subDepartment: activeSubDept }}
              onChange={{
                department: handleSelectDept,
                subDepartment: handleSelectSubDept,
              }}
              label={{ department: t('S030'), subDepartment: t('S029') }}
              defaultValue={{ department: t('S124'), subDepartment: t('S125') }}
              subDeptsArr={subDeptsArr}
              hasDepartmentError={hasFieldError('departmentRequired')}
              hasSubDepartmentError={hasFieldError('subDepartmentRequired')}
              departmentErrorText={getFieldError('departmentRequired')}
              subDepartmentErrorText={getFieldError('subDepartmentRequired')}
              isAfterSubmit={isAfterSubmit}
            />
            <Button isFullWidth className="margin-top-24" type="submit">
              {t('S017')}
            </Button>
          </form>
        </fieldset>
      )}
    </>
  );
}
